import pick from 'lodash/pick'
import { DropResult } from 'react-beautiful-dnd'
import store from 'redux/store'
import create from 'zustand'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ProjectKanbanViewClient } from 'api/clients/ProjectKanbanViewClient'
import { AssignedProperty } from 'api/clients/PropertyClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { fileToAssignee } from 'screens/Finder/Utils'
import { AlmanacFile, KanbanView, KanbanViewColumn } from 'types'
import { reportError } from 'utils/error'

const pickAttributes = (column: KanbanViewColumn) => {
  return pick(column, ['property_value_id', 'shown']) as unknown as KanbanViewColumn
}

const processUpdateResponse = (response: any) => {
  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    const error = new Error('Error updating kanban view')
    reportError(error, { severity: 'error', metaData: response })
    showToastMessage('There was a problem updating this view. Our team has been notified.', 'error')
    throw error
  }
}

export async function updateColumn(projectId: string, view: KanbanView, column?: KanbanViewColumn) {
  if (!column) return

  const res = await ProjectKanbanViewClient.updateColumn(projectId, view.id, pickAttributes(column))
  processUpdateResponse(res)
}

export async function updateColumns(projectId: string, view: KanbanView) {
  const res = await ProjectKanbanViewClient.update(projectId, view.id, view.columns.map(pickAttributes))
  processUpdateResponse(res)
}

export type Store = {
  view: KanbanView | null
  drop?: {
    fromAssignedProperty: AssignedProperty
    toAssignedProperty: AssignedProperty
  }
  isDraggingFile: boolean
  setView: (view: KanbanView | null) => void
  load: (currentFolder: AlmanacFile) => void
  reload: () => void
  moveFile: (
    result: DropResult,
  ) => { fromAssignedProperty: AssignedProperty; toAssignedProperty: AssignedProperty } | undefined
  toggleColumn: (column: KanbanViewColumn) => void
  clearDrop: () => void
  setIsDraggingFile: (isDraggingFile: boolean) => void
}

const useKanbanView = create<Store>((set, get) => ({
  view: null,
  isDraggingFile: false,
  setView: (view: KanbanView | null) => set(() => ({ view })),
  reload: async () => {
    const view = get().view
    if (view) {
      const res = await ProjectKanbanViewClient.get(view.project_key, view.id)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        get().setView(res.data.kanban_view)
      }
    }
  },
  load: async (currentFolder: AlmanacFile) => {
    if (!currentFolder || !currentFolder.status_kanban_view_id) return

    const res = await ProjectKanbanViewClient.get(currentFolder.id, currentFolder.status_kanban_view_id)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      get().setView(res.data.kanban_view)
    } else {
      reportError(new Error('Error loading kanban view'), { severity: 'error', metaData: res })
      showToastMessage('There was a problem loading this view. Our team has been notified.', 'error')
    }
  },
  toggleColumn: (column: KanbanViewColumn) => {
    const view = get().view

    if (!view) return

    column.shown = !column.shown

    updateColumn(view.project_key, view, column).catch(() => {
      // Rollback
      column.shown = !column.shown
      get().setView(view)
    })

    get().setView(view)
  },
  moveFile: (result: DropResult) => {
    const view = get().view

    if (!result.destination || !view || result.destination.droppableId === result.source.droppableId) return

    const [fileType, fileId] = result.draggableId.split('/')
    const property = store.getState().properties[view.property_id]
    const oldAssignedProperty = store.getState().filesProperties[fileId][property.id]
    const newPropertyValueId = parseInt(result.destination.droppableId)
    const newValue = property.property_values.filter((v) => v.id === newPropertyValueId)
    const assignee = fileToAssignee({ id: fileId, type: fileType } as AlmanacFile)

    if (!newValue.length) return

    const fromAssignedProperty = {
      id: oldAssignedProperty.id,
      property: property,
      property_values: oldAssignedProperty.values || [],
      assignee_id: assignee.id,
      assignee_type: assignee.type,
    } as AssignedProperty

    const toAssignedProperty = {
      id: oldAssignedProperty.id,
      property: property,
      property_values: newValue,
      assignee_id: assignee.id,
      assignee_type: assignee.type,
    } as AssignedProperty

    set(() => ({ drop: { fromAssignedProperty, toAssignedProperty } }))

    return { fromAssignedProperty, toAssignedProperty }
  },
  clearDrop: () => set(() => ({ drop: undefined })),
  setIsDraggingFile: (isDraggingFile: boolean) => set(() => ({ isDraggingFile })),
}))

export { useKanbanView }
